/**
 * OF-001 — Pending Invitations Banner
 *
 * Canonical surface for the LOGGED-IN user to discover, accept, and
 * reject organisation-membership invitations addressed to their email.
 *
 *   Reader:    GET  /api/auth/unified/invites/mine
 *   Accept:    POST /api/auth/unified/invite/accept   { invite_token }
 *   Reject:    POST /api/auth/unified/invite/reject   { invite_token }
 *
 * UX rules:
 *   - Mounted globally inside the authenticated app shell so every page
 *     surfaces pending invites without IA changes.
 *   - When the user has NO pending invitations, the component renders
 *     nothing. Patients with no invites see ZERO clutter (Phase E).
 *   - On accept, immediately refreshes /me so role_assignments + allowed
 *     workspaces update without re-login. Then performs a single
 *     soft route refresh.
 *   - Idempotent backend; component disables buttons during in-flight
 *     mutations to prevent double-click double-writes.
 */
import React, { useEffect, useState, useCallback } from 'react';
import api from '../utils/api';
import { useRBAC } from '../context/RBACContext';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { Mail, Check, X, Building2 } from 'lucide-react';
import { roleLabel } from '../utils/canonicalRoleLabels';

// OF-001D #3 — PendingInvitationsBanner consumes the canonical resolver.
// Local role label map removed; `roleLabel(role_id)` is the single source.

export default function PendingInvitationsBanner() {
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actingOn, setActingOn] = useState(null);
  // OF-001B — set the freshly-accepted workspace as the active one so the
  // user lands on it immediately AND the next login is deterministic.
  const { refreshProfile, setActiveWorkspace } = useRBAC();

  // OF-001C identity-isolation — the banner is mounted at App-level so it
  // never unmounts across login/logout transitions.  Without an identity
  // anchor, its `invites` React state from session A leaked into session
  // B (next user sees the previous user's invitations).  We anchor on the
  // STABLE authenticated user_id (charter §D: "must anchor on stable
  // authenticated user_id, not display name only").
  //
  // INVITE-VISIBILITY-01 — the anchor previously also required a
  // `tokenTail` read from a localStorage JWT. After the FIND-H-08 cutover
  // to an HttpOnly auth cookie the token is no longer readable, the
  // tail was hard-coded to `''`, and `isLoggedIn` was therefore
  // PERMANENTLY false — `fetchInvites` always short-circuited and the
  // banner rendered `null` for every user, so a pending invitation was
  // never visible. The readable user mirror is the correct and only
  // available client-side session signal.
  const [identity, setIdentity] = useState(() => {
    try {
      const u = JSON.parse(localStorage.getItem('treatx_unified_user') || 'null');
      return {
        userId: (u && (u.id || u.sub || u.user_id)) || null,
        email: (u && u.email) || null,
      };
    } catch {
      return { userId: null, email: null };
    }
  });
  const isLoggedIn = !!identity.userId;

  const fetchInvites = useCallback(async () => {
    if (!isLoggedIn) {
      setInvites([]);
      setLoading(false);
      return;
    }
    // OF-001C — capture identity at request-issue time so a late response
    // from a previous user cannot land in the current user's banner state.
    // Anchor is the user_id (stable across display-name changes) + email.
    const issuedForUserId = identity.userId;
    const issuedForEmail = identity.email;
    try {
      const res = await api.get('/auth/unified/invites/mine');
      // Re-read live identity at response time
      let liveUserId = null;
      let liveEmail = null;
      try {
        const u = JSON.parse(localStorage.getItem('treatx_unified_user') || 'null') || {};
        liveUserId = u.id || u.sub || u.user_id || null;
        liveEmail = u.email || null;
      } catch { /* ignore */ }
      if (liveUserId !== issuedForUserId || liveEmail !== issuedForEmail) {
        // Identity rotated mid-request — drop the response, do not pollute state.
        return;
      }
      // Defense-in-depth: server already scopes by JWT email, but verify
      // each row matches the live identity before surfacing.
      const filtered = (res.data?.invitations || []).filter(
        (inv) => (inv && (inv.email || '').toLowerCase()) === (liveEmail || '').toLowerCase()
      );
      setInvites(filtered);
    } catch {
      setInvites([]);
    } finally {
      setLoading(false);
    }
  }, [isLoggedIn, identity.userId, identity.email]);

  useEffect(() => { fetchInvites(); }, [fetchInvites]);

  // OF-001C identity-isolation — listen for identity transitions on the
  // `treatx:identity-changed` event AND across browser storage events
  // (multi-tab). Whenever the email/token in localStorage changes:
  //   1. Reset invites=[] immediately to flush stale state.
  //   2. Re-read identity from localStorage.
  //   3. fetchInvites will refire via its dep on `identity.email`.
  useEffect(() => {
    const syncIdentity = () => {
      try {
        const u = JSON.parse(localStorage.getItem('treatx_unified_user') || 'null');
        const nextUserId = (u && (u.id || u.sub || u.user_id)) || null;
        const nextEmail = (u && u.email) || null;
        setIdentity((prev) => {
          if (prev.userId !== nextUserId || prev.email !== nextEmail) {
            // Identity transition — flush all locally cached invites.
            setInvites([]);
            setLoading(true);
            return { userId: nextUserId, email: nextEmail };
          }
          return prev;
        });
      } catch {
        setIdentity({ userId: null, email: null });
        setInvites([]);
      }
    };
    window.addEventListener('treatx:identity-changed', syncIdentity);
    window.addEventListener('storage', syncIdentity);
    return () => {
      window.removeEventListener('treatx:identity-changed', syncIdentity);
      window.removeEventListener('storage', syncIdentity);
    };
  }, []);

  // OF-001C — runtime refresh triggers so an invitation sent in another
  // session becomes visible WITHOUT requiring logout/login. We use:
  //   1. `focus`           — tab regains focus
  //   2. `visibilitychange`— tab becomes visible again
  //   3. `treatx:identity-changed` — explicit dispatch (login, accept, etc.)
  //   4. low-frequency polling (60s) — bounded safety net
  // Combined, these cover the realistic windows in which a new invite
  // could land while the patient is still logged in, without burning
  // server resources with aggressive polling.
  useEffect(() => {
    if (!isLoggedIn) return undefined;
    const onFocus = () => { fetchInvites(); };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') fetchInvites();
    };
    const onIdentityChanged = () => { fetchInvites(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('treatx:identity-changed', onIdentityChanged);
    // Lightweight polling — 60s is enough for an owner-test scenario
    // (invite sent in another browser becomes visible within a minute).
    const intervalId = setInterval(() => {
      if (document.visibilityState === 'visible') fetchInvites();
    }, 60_000);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('treatx:identity-changed', onIdentityChanged);
      clearInterval(intervalId);
    };
  }, [isLoggedIn, fetchInvites]);

  const refreshIdentity = async () => {
    try {
      const me = await api.get('/auth/unified/me');
      if (me?.data) {
        // Mirror what the login flow stores so role-gated UIs see the
        // freshly-activated role/clinic without a re-login.
        try {
          localStorage.setItem('treatx_unified_user', JSON.stringify(me.data));
        } catch { /* ignore quota */ }
      }
    } catch { /* ignore */ }
  };

  const accept = async (token, label) => {
    setActingOn(token);
    try {
      const res = await api.post('/auth/unified/invite/accept', { invite_token: token });
      if (res.data?.success) {
        const recommended = res.data.recommended_workspace;
        // OF-001B — refresh canonical /me FIRST so the new workspace is in
        // RBACContext.workspaces[] before we try to activate it.
        await refreshProfile();
        await refreshIdentity();
        await fetchInvites();
        window.dispatchEvent(new Event('treatx:identity-changed'));

        if (recommended?.is_available && recommended?.default_route) {
          // Activate the invited workspace and route to its canonical page.
          const applied = await setActiveWorkspace(recommended.workspace_id);
          toast.success(
            `Joined as ${roleLabel(label)}. Opening ${recommended.display_name}…`
          );
          // Soft hash navigation — preserves React Router state, no full reload.
          window.location.hash = (applied?.default_route) || recommended.default_route;
        } else if (recommended && !recommended.is_available) {
          toast.message(
            `Joined as ${roleLabel(label)}, but no workspace route is configured for this role.`
          );
        } else {
          toast.success(`Joined as ${roleLabel(label)}. Workspace updated.`);
        }
      } else {
        toast.error(res.data?.message || 'Could not accept invitation');
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data?.detail || 'Could not accept invitation';
      toast.error(typeof msg === 'string' ? msg : 'Could not accept invitation');
    } finally {
      setActingOn(null);
    }
  };

  const reject = async (token) => {
    setActingOn(token);
    try {
      const res = await api.post('/auth/unified/invite/reject', { invite_token: token });
      if (res.data?.success) {
        toast.message('Invitation declined.');
        await fetchInvites();
      } else {
        toast.error('Could not decline invitation');
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data?.detail || 'Could not decline invitation';
      toast.error(typeof msg === 'string' ? msg : 'Could not decline invitation');
    } finally {
      setActingOn(null);
    }
  };

  if (loading || !isLoggedIn || invites.length === 0) return null;

  return (
    <div
      className="fixed top-4 right-4 z-[60] max-w-md w-[92vw] sm:w-[420px] space-y-2"
      data-testid="pending-invitations-banner"
    >
      {invites.map((inv) => (
        <Card key={inv.token} className="border-2 border-emerald-300 shadow-lg bg-white">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                <Mail className="h-4 w-4 text-emerald-700" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Building2 className="h-3.5 w-3.5 text-slate-500" />
                  <span className="text-sm font-semibold text-slate-900 truncate" data-testid={`invite-clinic-${inv.token}`}>
                    {inv.clinic_name || inv.clinic_id}
                  </span>
                </div>
                <div className="text-xs text-slate-600 mt-0.5">
                  invited you as{' '}
                  <Badge className="bg-emerald-100 text-emerald-800 text-[10px] align-middle" data-testid={`invite-role-${inv.token}`}>
                    {roleLabel(inv.role)}
                  </Badge>
                </div>
                {inv.invited_by && (
                  <p className="text-[11px] text-slate-400 mt-0.5">
                    From <span data-testid={`invite-inviter-${inv.token}`}>{inv.invited_by}</span>
                  </p>
                )}
                {inv.custom_message && (
                  <p className="text-[11px] text-slate-500 italic mt-1">“{inv.custom_message}”</p>
                )}
                <div className="flex gap-2 mt-3">
                  <Button
                    size="sm"
                    className="h-7 px-3 text-[11px] bg-emerald-600 hover:bg-emerald-700"
                    onClick={() => accept(inv.token, inv.role)}
                    disabled={actingOn === inv.token}
                    data-testid={`invite-accept-${inv.token}`}
                  >
                    <Check className="h-3 w-3 mr-1" /> Accept
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 px-3 text-[11px] text-rose-700 border-rose-300 hover:bg-rose-50"
                    onClick={() => reject(inv.token)}
                    disabled={actingOn === inv.token}
                    data-testid={`invite-reject-${inv.token}`}
                  >
                    <X className="h-3 w-3 mr-1" /> Decline
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
